import { addDays, diffJours } from './dates'

// Moteur du modèle « durée libre » : le client choisit une DATE DE DÉPART + une DURÉE (en jours).
// Journée = 1, 2 jours = 2, 3 jours = 3. Au-delà de 3 jours : prix = prix 3 jours + (n−3) × jour supplémentaire.
// Remplace l'ancien src/utils/rotations.js (modèle rotations/créneaux). Voir docs/plan-bascule-duree-libre.md.
// Style UTC + chaînes 'AAAA-MM-JJ' (via dates.js) — pas de souci de fuseau.

// Formules « socle » proposées par défaut dans le tunnel (la Journée est la formule phare).
export const DUREE_PRESETS = [
  { duree: 1, key: 'journee' },
  { duree: 2, key: 'deux_jours' },
  { duree: 3, key: 'trois_jours' },
]

// Clé de formule socle pour une durée (libellé + prix de base). 3 jours et plus → socle 'trois_jours'.
export function formuleKeyFromDuree(n) {
  const d = Math.floor(Number(n) || 0)
  if (d <= 1) return 'journee'
  if (d === 2) return 'deux_jours'
  return 'trois_jours'
}

// Libellé lisible d'une durée ('Journée', '2 jours', '5 jours'…).
export function dureeLabel(n, formules = {}) {
  const d = Math.floor(Number(n) || 0)
  if (d <= 3) return formules?.[formuleKeyFromDuree(d)]?.label || `${d} jour${d > 1 ? 's' : ''}`
  return `${d} jours`
}

// Prix d'UNE planche pour une durée et un tarif ('public' | 'sejour').
// n≥3 : prix 3 jours + (n−3) × prix du jour supplémentaire.
export function prixFormule(duree, tarif, { formules = {}, prixJourSupp = {} } = {}) {
  const n = Math.max(1, Math.floor(Number(duree) || 0))
  const key = tarif === 'sejour' ? 'prix_sejour' : 'prix_public'
  if (n === 1) return Number(formules?.journee?.[key]) || 0
  if (n === 2) return Number(formules?.deux_jours?.[key]) || 0
  const base = Number(formules?.trois_jours?.[key]) || 0
  const supp = Number(prixJourSupp?.[key]) || 0
  return base + (n - 3) * supp
}

// Dates d'une location. date_fin = BORNE EXCLUSIVE de dispo = date_debut + durée.
// Occupation half-open [date_debut, date_fin) → le lendemain de la remise est déjà réutilisable.
// ⚠️ date_fin n'est PAS le jour de remise : la planche est rendue le SOIR du dernier jour d'usage
//    (= date_debut + durée − 1). Utiliser remiseDay() pour tout AFFICHAGE / pointage de retour.
export function datesFromStart(start, duree) {
  const n = Math.max(1, Math.floor(Number(duree) || 0))
  return { date_debut: start, date_fin: addDays(start, n) }
}

// Jour de REMISE (retour physique, le soir) = dernier jour d'usage = date_debut + durée − 1.
// À utiliser partout pour l'AFFICHAGE et le pointage « retour du jour » (pas date_fin, qui est exclusive).
export function remiseDay(start, duree) {
  if (!start) return ''
  const n = Math.max(1, Math.floor(Number(duree) || 0))
  return addDays(start, n - 1)
}

// Jour de remise déduit d'une réservation (gère les alias de champs start/durée/fin des différents écrans).
export function remiseDayFromContract(c = {}) {
  const start = String(c.startDate || c.date_debut || '').split('T')[0]
  if (!start) return ''
  const end = c.endDate || c.date_fin || ''
  const dur = Number(c.duree_jours ?? c.duree ?? c.dureeJours) || (end ? diffJours(start, end) : 0) || 1
  return remiseDay(start, dur)
}

// Une location est-elle entièrement dans la saison ? (retour ≤ dernier retour autorisé)
export function departValide(start, duree, { start: saisonStart, lastReturn } = {}) {
  if (!start || !saisonStart || !lastReturn) return false
  if (start < saisonStart) return false
  const { date_fin } = datesFromStart(start, duree)
  return date_fin <= lastReturn
}

// Liste des jours de départ possibles (ISO) pour une durée donnée : de max(saison.start, minDate)
// jusqu'au dernier départ dont le retour reste ≤ lastReturn.
export function joursDepart({ start, lastReturn } = {}, duree = 1, minDate) {
  if (!start || !lastReturn) return []
  const n = Math.max(1, Math.floor(Number(duree) || 0))
  const from = minDate && minDate > start ? minDate : start
  const dernierDepart = addDays(lastReturn, -n)
  const jours = []
  let d = from
  while (d <= dernierDepart) { jours.push(d); d = addDays(d, 1) }
  return jours
}
